import axios, {AxiosInstance} from 'axios'
import type {
    PublicAccessToken,
    Credentials,
    BookmarkResponse,
    BookmarkStats,
    Bookmark
} from '@/types/api'

class ApiClient {
    private client: AxiosInstance

    constructor() {
        this.client = axios.create({
            baseURL: '/api',
            headers: {
                'Content-Type': 'application/json'
            }
        })

        // Add auth token to requests if available
        this.client.interceptors.request.use((config) => {
            const token = localStorage.getItem('access_token')
            if (token) {
                config.headers.Authorization = `Bearer ${token}`
            }
            return config
        })

        // Handle token refresh on 401
        this.client.interceptors.response.use(
            (response) => response,
            async (error) => {
                if (error.response?.status === 401) {
                    // Try to refresh token
                    const token = localStorage.getItem('access_token')
                    if (token) {
                        try {
                            const refreshResponse = await this.refreshToken()
                            localStorage.setItem('access_token', refreshResponse.access_token)
                            // Retry original request
                            error.config.headers.Authorization = `Bearer ${refreshResponse.access_token}`
                            return this.client(error.config)
                        } catch {
                            // Refresh failed, redirect to login
                            localStorage.removeItem('access_token')
                            window.location.href = '/login'
                        }
                    }
                }
                return Promise.reject(error)
            }
        )
    }

    // Auth endpoints
    async login(credentials: Credentials): Promise<PublicAccessToken> {
        const response = await this.client.post<PublicAccessToken>('/token', credentials)
        return response.data
    }

    async logout(): Promise<void> {
        await this.client.post('/logout')
        localStorage.removeItem('access_token')
    }

    async refreshToken(): Promise<PublicAccessToken> {
        const response = await this.client.post<PublicAccessToken>('/refresh-session')
        return response.data
    }

    // Public endpoints (no auth required for read-only)
    async getPublicBookmarks(page = 1, itemsPerPage = 21, onlyFetched = true): Promise<BookmarkResponse> {
        const response = await this.client.get<BookmarkResponse>('/bookmarks', {
            params: {
                current_page: page,
                items_per_page: itemsPerPage,
                only_fetched: onlyFetched,
                order_by: '-date'  // Use backend's expected format

            }
        })
        return response.data
    }

    async getRandomBookmark(): Promise<Bookmark> {
        const response = await this.client.get<BookmarkResponse>('/bookmarks', {
            params: {
                items_per_page: 1,
                order_by: 'random'
            }
        })
        return response.data.items[0]
    }

    // Protected endpoints
    async getBookmarkStats(): Promise<BookmarkStats> {
        const response = await this.client.get<BookmarkStats>('/bookmarks/stats')
        return response.data
    }

    async addBookmark(url: string): Promise<Bookmark> {
        const response = await this.client.post<Bookmark>('/bookmarks', {url})
        return response.data
    }

    async deleteBookmark(id: string): Promise<Bookmark> {
        const response = await this.client.delete<Bookmark>(`/bookmarks/${id}`)
        return response.data
    }

    async updateBookmark(id: string): Promise<Bookmark> {
        const response = await this.client.put<Bookmark>(`/bookmarks/${id}`)
        return response.data
    }
}

export const api = new ApiClient()
